/**
 * 连通域标记（两遍 + 并查集，8 连通）与分量统计 —— 逐行对拍 climb-core/src/conn.rs
 */

export class CompStats {
  label = 0;
  area = 0;
  x0 = 0;
  y0 = 0;
  x1 = 0;
  y1 = 0;
  sumX = 0;
  sumY = 0;
  /** 彩色像素（sat>=0.22 且 v>=0.10）计数 */
  colored = 0;
  /** 彩色像素色相直方图（72 桶 × 5°） */
  hueBins = new Uint32Array(72);
  satSum = 0;
  valSum = 0;

  centroid(): [number, number] {
    const a = Math.max(this.area, 1);
    return [this.sumX / a, this.sumY / a];
  }
  bboxArr(): [number, number, number, number] {
    return [this.x0, this.y0, this.x1, this.y1];
  }
  bw(): number {
    return Math.max(this.x1 - this.x0, 0) || 1;
  }
  bh(): number {
    return Math.max(this.y1 - this.y0, 0) || 1;
  }
  fill(): number {
    return this.area / (this.bw() * this.bh());
  }
  aspect(): number {
    const bw = this.bw();
    const bh = this.bh();
    return Math.max(bw, bh) / Math.max(Math.min(bw, bh), 1);
  }
  satMean(): number {
    return this.satSum / Math.max(this.area, 1);
  }
  valMean(): number {
    return this.valSum / Math.max(this.area, 1);
  }
  coloredFrac(): number {
    return this.colored / Math.max(this.area, 1);
  }
  /** 众数色相与纯度（众数 ±10° 窗口内像素占彩色像素比例） */
  modeHue(): [number, number] {
    if (this.colored === 0) return [0, 0];
    let best = 0;
    for (let i = 1; i < 72; i++) {
      if (this.hueBins[i] > this.hueBins[best]) best = i;
    }
    let win = 0;
    for (let d = -2; d <= 2; d++) {
      const idx = (((best + d) % 72) + 72) % 72;
      win += this.hueBins[idx];
    }
    return [best * 5 + 2.5, win / this.colored];
  }
}

class UnionFind {
  parent: Uint32Array;
  count = 0;
  constructor() {
    this.parent = new Uint32Array(1024);
  }
  private ensure(n: number): void {
    if (n < this.parent.length) return;
    let cap = this.parent.length;
    while (cap <= n) cap *= 2;
    const next = new Uint32Array(cap);
    next.set(this.parent);
    this.parent = next;
  }
  find(x: number): number {
    while (this.parent[x] !== x) {
      this.parent[x] = this.parent[this.parent[x]];
      x = this.parent[x];
    }
    return x;
  }
  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[rb] = ra;
  }
  /** 新建标签，返回其 id */
  create(): number {
    this.count++;
    this.ensure(this.count + 1);
    this.parent[this.count] = this.count;
    return this.count;
  }
}

/**
 * 8 连通连通域标记。返回 { labels, count }，labels[i]==0 为背景。
 * 标签编号压缩为连续 1..count（编号顺序与上游一致：按并查集根首次出现顺序）。
 */
export function labelComponents(
  mask: Uint8Array,
  w: number,
  h: number,
): { labels: Uint32Array; count: number } {
  if (mask.length !== w * h) throw new Error("mask size mismatch");
  const labels = new Uint32Array(w * h);
  const uf = new UnionFind();

  for (let y = 0; y < h; y++) {
    const top = y > 0;
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (mask[i] === 0) continue;
      let m = 0;
      if (x > 0 && labels[i - 1] !== 0) m = labels[i - 1];
      if (top) {
        if (x > 0 && labels[i - w - 1] !== 0) {
          const l = labels[i - w - 1];
          if (m === 0) m = l;
          else if (l !== m) uf.union(m, l);
        }
        if (labels[i - w] !== 0) {
          const l = labels[i - w];
          if (m === 0) m = l;
          else if (l !== m) uf.union(m, l);
        }
        if (x + 1 < w && labels[i - w + 1] !== 0) {
          const l = labels[i - w + 1];
          if (m === 0) m = l;
          else if (l !== m) uf.union(m, l);
        }
      }
      if (m === 0) {
        labels[i] = uf.create();
      } else {
        labels[i] = m;
      }
    }
  }

  // 压缩为连续编号（按根标签首次出现顺序，与上游 map 遍历一致）
  const map = new Map<number, number>();
  let cnt = 0;
  for (let l = 1; l <= uf.count; l++) {
    const r = uf.find(l);
    if (!map.has(r)) {
      map.set(r, ++cnt);
    }
  }
  for (let i = 0; i < labels.length; i++) {
    if (labels[i] !== 0) {
      labels[i] = map.get(uf.find(labels[i]))!;
    }
  }
  return { labels, count: cnt };
}

/** 汇总每个分量的统计（含色相直方图）。返回数组按 label-1 索引 */
export function gatherStats(
  labels: Uint32Array,
  hue: Float32Array,
  sat: Float32Array,
  val: Float32Array,
  w: number,
  h: number,
  cnt: number,
): CompStats[] {
  const out: CompStats[] = [];
  for (let i = 0; i < cnt; i++) {
    const s = new CompStats();
    s.label = i + 1;
    s.x0 = 0xffffffff;
    s.y0 = 0xffffffff;
    out.push(s);
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const l = labels[i];
      if (l === 0) continue;
      const s = out[l - 1];
      s.area++;
      s.sumX += x;
      s.sumY += y;
      if (x < s.x0) s.x0 = x;
      if (y < s.y0) s.y0 = y;
      if (x + 1 > s.x1) s.x1 = x + 1;
      if (y + 1 > s.y1) s.y1 = y + 1;
      if (sat[i] >= 0.22 && val[i] >= 0.1) {
        s.colored++;
        const b = (((Math.floor(hue[i] / 5) % 72) + 72) % 72) | 0;
        s.hueBins[b]++;
      }
      s.satSum += sat[i];
      s.valSum += val[i];
    }
  }
  return out;
}

/**
 * 多种子测地重构（8 连通 BFS）：掩码内把每像素分配给测地距离最近的种子块
 * （并列先到先得），caps 限制各种子块最终面积上限。对拍 conn.rs:222-272
 */
export function voronoiReconstruct(
  samMask: Uint8Array,
  w: number,
  h: number,
  seeds: Uint32Array[],
  caps: number[],
): Uint32Array[] {
  if (samMask.length !== w * h) throw new Error("mask size mismatch");
  if (seeds.length !== caps.length) throw new Error("seeds/caps mismatch");
  const owner = new Uint32Array(w * h);
  const queue: number[] = [];
  const counts: number[] = [];
  for (let li = 0; li < seeds.length; li++) {
    const l = li + 1;
    let c = 0;
    for (const i0 of seeds[li]) {
      const i = i0;
      if (i < w * h && samMask[i] > 0 && owner[i] === 0) {
        owner[i] = l;
        queue.push(i);
        c++;
      }
    }
    counts.push(c);
  }
  let head = 0;
  while (head < queue.length) {
    const i = queue[head++];
    const l = owner[i];
    const li = l - 1;
    if (counts[li] >= caps[li]) continue; // 达上限停止扩张
    const x = i % w;
    const y = Math.floor(i / w);
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const ni = ny * w + nx;
        if (samMask[ni] > 0 && owner[ni] === 0) {
          owner[ni] = l;
          counts[li]++;
          queue.push(ni);
        }
      }
    }
  }
  const out: Uint32Array[] = [];
  for (let i = 0; i < seeds.length; i++) out.push(new Uint32Array(0));
  const tmp: number[][] = seeds.map(() => []);
  for (let i = 0; i < owner.length; i++) {
    const l = owner[i];
    if (l > 0) tmp[l - 1].push(i);
  }
  for (let i = 0; i < seeds.length; i++) out[i] = Uint32Array.from(tmp[i]);
  return out;
}
